export type TimelineKind = 'official' | 'schedule' | 'attendee';
export type TimelineSource = { label: string; url: string };
export type TimelineEntry = {
  id: string;
  title: string;
  summary: string;
  kind: TimelineKind;
  sortAt: string;
  timeLabel: string;
  timeBasis: 'issued' | 'reported' | 'scheduled' | 'checked';
  occurredAt: string | null;
  issuedAt?: string;
  reportedAt?: string;
  editedAt?: string;
  scheduledAt?: string;
  effectiveAt?: string;
  source: TimelineSource;
  additionalSources?: TimelineSource[];
  note?: string;
};

export const TIMELINE_CHECKED_AT = '2026-09-21T08:21:47Z';
export const TIMELINE_KIND_LABELS: Record<TimelineKind, string> = {
  official: 'Official record',
  schedule: 'Published schedule',
  attendee: 'Attendee account',
};
export const TIMELINE_METHOD = 'All times are Eastern. Weather bulletins link to the Iowa Environmental Mesonet archive. Attendee entries use their public posting times and remain unverified. Schedules show planned times; undated pages show when we checked them.';

export const TIMELINE_ENTRIES: readonly TimelineEntry[] = [
  {
    id: 'september-16-rain-forecast',
    title: 'NWS identifies an overnight heavy-rain risk',
    summary: 'The Wilmington forecast discussion describes possible heavy rain near and north of I-70 overnight into Thursday. It lists a flood watch including Licking County and says the exact storm track remains uncertain.',
    kind: 'official',
    sortAt: '2026-09-16T19:05:00Z',
    timeLabel: 'Issued Sep 16, 2026 at 3:05 PM EDT',
    timeBasis: 'issued',
    occurredAt: null,
    issuedAt: '2026-09-16T19:05:00Z',
    effectiveAt: '2026-09-17T04:00:00Z',
    source: { label: 'NWS Wilmington forecast discussion · IEM archive', url: 'https://mesonet.agron.iastate.edu/p.php?pid=202609161905-KILN-FXUS61-AFDILN' },
    note: 'The watch begins at midnight Thursday. Forecast rainfall covers the surrounding region.',
  },
  {
    id: 'saturday-afternoon-forecast',
    title: 'Saturday forecast includes Sunday-night heavy rain',
    summary: 'NWS discusses Saturday storms with possible damaging winds, then identifies the I-70 corridor as the area with the greatest Sunday-night heavy-rain potential. Regional model guidance includes a possibility of more than three inches in three hours.',
    kind: 'official',
    sortAt: '2026-09-19T18:34:00Z',
    timeLabel: 'Issued Sep 19, 2026 at 2:34 PM EDT',
    timeBasis: 'issued',
    occurredAt: null,
    issuedAt: '2026-09-19T18:34:00Z',
    source: { label: 'NWS Wilmington forecast discussion · IEM archive', url: 'https://mesonet.agron.iastate.edu/p.php?pid=202609191834-KILN-FXUS61-AFDILN' },
    note: 'Storm timing and rainfall at Legend Valley remained uncertain.',
  },
  {
    id: 'saturday-shuttle-questions',
    title: 'Attendees ask about shuttles during Saturday evacuation',
    summary: 'A public festival-forum post asks whether shuttles have closed while the festival is evacuated. The discussion records confusion about transport during the interruption.',
    kind: 'attendee',
    sortAt: '2026-09-19T21:28:24Z',
    timeLabel: 'Reported Sep 19, 2026 at 5:28:24 PM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-19T21:28:24Z',
    source: { label: 'Public attendee post · “Shuttles closed?”', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wky5za/shuttles_closed/' },
    note: 'Shuttle status and the evacuation-order time remain unconfirmed in this account.',
  },
  {
    id: 'saturday-reentry-report',
    title: 'Rider reports leaving a shuttle and approaching security',
    summary: 'A commenter says their group was let off its shuttle around 6:30 PM and was approaching security. Another attendee in the same thread later reports entering.',
    kind: 'attendee',
    sortAt: '2026-09-19T22:34:13Z',
    timeLabel: 'Reported Sep 19, 2026 at 6:34:13 PM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-19T22:34:13Z',
    source: { label: 'Public attendee comment · Saturday updates', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wkzjda/any_updates/paupwxu/' },
    additionalSources: [{ label: 'Second attendee comment · edited at 6:35:18 PM EDT', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wkzjda/any_updates/pauooxi/' }],
    note: 'The second comment was edited after its initial publication. A festival-wide reopening time remains unverified.',
  },
  {
    id: 'sunday-wpc-outlook',
    title: 'National rainfall outlook describes sensitive ground conditions',
    summary: 'The Weather Prediction Center describes an excessive-rainfall risk in parts of the Midwest and Ohio Valley. It connects the risk to prior rainfall, additional storms and the potential for heavier regional totals.',
    kind: 'official',
    sortAt: '2026-09-20T08:32:00Z',
    timeLabel: 'Issued Sep 20, 2026 at 4:32 AM EDT',
    timeBasis: 'issued',
    occurredAt: null,
    issuedAt: '2026-09-20T08:32:00Z',
    effectiveAt: '2026-09-20T12:00:00Z',
    source: { label: 'NWS Weather Prediction Center · IEM archive', url: 'https://mesonet.agron.iastate.edu/p.php?pid=202609200832-KWBC-FOUS30-QPFERD' },
    note: 'The regional outlook covers 8 AM Sunday through 8 AM Monday.',
  },
  {
    id: 'sunday-afternoon-watch',
    title: 'Afternoon discussion lists an evening flood watch',
    summary: 'NWS forecasts additional overnight storms, heavy rain and possible flooding. Its watch list includes Licking County (OHZ056), effective from 7 PM through Monday morning. The discussion also identifies a conditional damaging-wind threat.',
    kind: 'official',
    sortAt: '2026-09-20T18:41:00Z',
    timeLabel: 'Issued Sep 20, 2026 at 2:41 PM EDT',
    timeBasis: 'issued',
    occurredAt: null,
    issuedAt: '2026-09-20T18:41:00Z',
    effectiveAt: '2026-09-20T23:00:00Z',
    source: { label: 'NWS Wilmington forecast discussion · IEM archive', url: 'https://mesonet.agron.iastate.edu/p.php?pid=202609201841-KILN-FXUS61-AFDILN' },
    note: 'The discussion was issued at 2:41 PM. The original watch-issuance time needs separate verification. The precise heavy-rain axis remained uncertain.',
  },
  {
    id: 'sunday-public-weather-discussion',
    title: 'Festival forum shares concern about the evening forecast',
    summary: 'A public post discusses the evening flood watch and rainfall outlook with other festival attendees.',
    kind: 'attendee',
    sortAt: '2026-09-20T20:48:00Z',
    timeLabel: 'Reported Sep 20, 2026 at 4:48 PM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-20T20:48:00Z',
    source: { label: 'Public forum post · Evening weather', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wlsgpo/weather_not_looking_good_coming_into_the_evening/' },
    note: 'The post was publicly available at 4:48 PM.',
  },
  {
    id: 'sunday-evening-forecast',
    title: 'Evening forecast continues the storm and flooding outlook',
    summary: 'The updated NWS discussion describes an initial risk of damaging wind and large hail, followed by continued heavy-rain and flooding potential overnight. The flood watch remains listed through Monday morning.',
    kind: 'official',
    sortAt: '2026-09-20T23:34:00Z',
    timeLabel: 'Issued Sep 20, 2026 at 7:34 PM EDT',
    timeBasis: 'issued',
    occurredAt: null,
    issuedAt: '2026-09-20T23:34:00Z',
    source: { label: 'NWS Wilmington forecast discussion · IEM archive', url: 'https://mesonet.agron.iastate.edu/p.php?pid=202609202334-KILN-FXUS61-AFDILN' },
    note: 'The outlook covers the surrounding region.',
  },
  {
    id: 'sunday-headliner-scheduled-start',
    title: 'Published timetable lists the Sunday headliner at 9:40 PM',
    summary: 'The published timetable reproduction lists Excision b2b Space Laces at the Prehistoric stage at 9:40 PM Sunday.',
    kind: 'schedule',
    sortAt: '2026-09-21T01:40:00Z',
    timeLabel: 'Scheduled Sep 20, 2026 at 9:40 PM EDT',
    timeBasis: 'scheduled',
    occurredAt: null,
    scheduledAt: '2026-09-21T01:40:00Z',
    source: { label: 'FestTimetable · September 16 timetable reproduction', url: 'https://festtimetable.com/en-US/articles/lostlandsfestival-2026/' },
    additionalSources: [{ label: 'Lost Lands official app and schedule information', url: 'https://www.lostlandsfestival.com/app/' }],
    note: 'Actual performance start and end times remain unverified.',
  },
  {
    id: 'sunday-dino-express-return-schedule',
    title: 'Published Dino Express return window begins at 11 PM',
    summary: 'The festival’s Friday–Sunday schedule lists return buses to Columbus every 30 minutes from 11 PM to 4:30 AM. It names Bus.com as the Dino Express provider.',
    kind: 'schedule',
    sortAt: '2026-09-21T03:00:00Z',
    timeLabel: 'Scheduled Sep 20, 2026 at 11:00 PM EDT',
    timeBasis: 'scheduled',
    occurredAt: null,
    scheduledAt: '2026-09-21T03:00:00Z',
    source: { label: 'Lost Lands · 2026 hotels and shuttles', url: 'https://www.lostlandsfestival.com/hotels-shuttles/' },
    note: 'Actual departures during the storm remain unverified. Dino Express, hotel-package buses and parking-lot shuttles are separate services.',
  },
  {
    id: 'sunday-late-closure-reports',
    title: 'Late-night comments describe closure announcements and activity',
    summary: 'One attendee says they believe they can hear an announcement from Easy Camping. Another, writing 20 seconds later from a shuttle queue, says the Crater appears closed and that they have not seen an official notice.',
    kind: 'attendee',
    sortAt: '2026-09-21T03:53:17Z',
    timeLabel: 'Reported Sep 20, 2026 at 11:53:17 PM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-21T03:53:17Z',
    source: { label: 'Public attendee comment · Easy Camping', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wm1wcc/fest_over/pb3jf3j/' },
    additionalSources: [{ label: 'Public attendee comment · Shuttle queue, 11:53:37 PM EDT', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wm1wcc/fest_over/pb3jh2b/' }],
    note: 'The exact Sunday stop, closure and evacuation-order times remain unverified.',
  },
  {
    id: 'monday-stationary-shuttle-report',
    title: 'Racetrack rider reports a shuttle that is not moving',
    summary: 'A rider in the Racetrack shuttle discussion reports being on a stationary bus and describes uncertainty about the delay. Other commenters report waiting nearby.',
    kind: 'attendee',
    sortAt: '2026-09-21T04:24:34Z',
    timeLabel: 'Reported Sep 21, 2026 at 12:24:34 AM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-21T04:24:34Z',
    source: { label: 'Public attendee comment · Racetrack shuttles', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wm2j3d/racetrack_shuttles/pb3o7fg/' },
    note: 'The account leaves boarding time, the cause of the delay and movement elsewhere on the route unclear.',
  },
  {
    id: 'monday-tow-wait-report',
    title: 'A14 attendee reports a long wait for recovery',
    summary: 'An attendee says they have waited about five hours for a tow with their hood raised in A14.',
    kind: 'attendee',
    sortAt: '2026-09-21T07:32:53Z',
    timeLabel: 'Reported Sep 21, 2026 at 3:32:53 AM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-21T07:32:53Z',
    source: { label: 'Public attendee post · A14 towing wait', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wm5zf8/waiting_for_tow_for_5_hours/' },
    note: 'The five-hour wait is the attendee’s estimate.',
  },
  {
    id: 'monday-recovery-trucks-report',
    title: 'Another attendee reports recovery trucks at Racetrack',
    summary: 'A commenter reports two trucks working in their part of the Racetrack lot while other participants describe not seeing trucks where they are waiting.',
    kind: 'attendee',
    sortAt: '2026-09-21T07:35:24Z',
    timeLabel: 'Reported Sep 21, 2026 at 3:35:24 AM EDT',
    timeBasis: 'reported',
    occurredAt: null,
    reportedAt: '2026-09-21T07:35:24Z',
    source: { label: 'Public attendee comment · Recovery updates', url: 'https://www.reddit.com/r/LostLandsMusicFest/comments/1wm5ovc/updates_thread/pb4cjxq/' },
    note: 'The report describes one part of the lot.',
  },
  {
    id: 'festival-exit-page-snapshot',
    title: 'Festival exit page describes wet ground and slow departures',
    summary: 'Team Lost Lands advises campers to expect a slow Monday exit, identifies camping zones that may have long periods without movement, and states that recovery from wet ground is free with the vehicle hood raised. The page also lists shuttle and exit arrangements.',
    kind: 'official',
    sortAt: TIMELINE_CHECKED_AT,
    timeLabel: 'Checked Sep 21, 2026 at 4:21:47 AM EDT',
    timeBasis: 'checked',
    occurredAt: null,
    source: { label: 'Lost Lands · Official exit instructions', url: 'https://www.lostlandsfestival.com/exit/' },
    note: 'The page has no visible publication or update timestamp. Current queue lengths are unavailable.',
  },
  {
    id: 'monday-cristland-published-restriction',
    title: 'Exit plan lists a 6–9 AM Cristland Hill restriction',
    summary: 'The published Monday exit instructions direct affected campers toward Licking Trails Road during the 6–9 AM restriction on the Cristland Hill Road exit.',
    kind: 'schedule',
    sortAt: '2026-09-21T10:00:00Z',
    timeLabel: 'Scheduled Sep 21, 2026 at 6:00 AM EDT',
    timeBasis: 'scheduled',
    occurredAt: null,
    scheduledAt: '2026-09-21T10:00:00Z',
    source: { label: 'Lost Lands · Published Monday exit plan', url: 'https://www.lostlandsfestival.com/exit/' },
    note: 'The restriction was scheduled for after this review’s cutoff. Check the current exit instructions for road conditions.',
  },
];

// The compact background record focuses on warning lead time. Individual
// transport anecdotes and routine set/shuttle schedules remain addressable by
// old links, but do not appear as selected milestones.
const KEY_TIMELINE_IDS = new Set([
  'saturday-afternoon-forecast',
  'sunday-afternoon-watch',
  'sunday-public-weather-discussion',
  'sunday-evening-forecast',
]);
export const KEY_TIMELINE_ENTRIES: readonly TimelineEntry[] = TIMELINE_ENTRIES
  .filter(entry => KEY_TIMELINE_IDS.has(entry.id))
  .sort((a, b) => Date.parse(b.sortAt) - Date.parse(a.sortAt));

export function selectTimelineEntries(
  entries: readonly TimelineEntry[],
  kind: TimelineKind | 'all' = 'all',
  query = '',
  order: 'oldest' | 'newest' = 'newest',
): TimelineEntry[] {
  const needle = query.trim().toLocaleLowerCase();
  return entries.filter((entry) => {
    if (kind !== 'all' && entry.kind !== kind) return false;
    const words = [entry.title, entry.summary, entry.note, entry.source.label, entry.timeLabel];
    return !needle || words.join(' ').toLocaleLowerCase().includes(needle);
  }).sort((a, b) => {
    const chronological = Date.parse(a.sortAt) - Date.parse(b.sortAt) || a.id.localeCompare(b.id);
    return order === 'oldest' ? chronological : -chronological;
  });
}

export function timelineDayKey(timestamp: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(timestamp));
  const part = (name: string) => parts.find((p) => p.type === name)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
